/**
 * Paged book reader with optimistic streaming UI.
 *
 * Renders the current page's text as it streams in and shows an
 * aspect-ratio-locked shimmer for its illustration until `image_ready` arrives
 * (no layout shift). When the current page's text is done, the next page is
 * pre-rendered as a faint skeleton so the book feels like it is "drawing itself".
 */

import { useEffect, useMemo, useState } from "react";

import type { Book } from "@/lib/api";

import { ImagePlaceholder } from "./ImagePlaceholder";
import { ReadAloud } from "./ReadAloud";
import { useBookStream, type StreamingPage } from "./useBookStream";

interface BookReaderProps {
  /** SSE stream URL for a generating book, or null when reading from cache. */
  streamUrl: string | null;
  /** Pre-loaded pages when re-reading an already-generated (cached) book. */
  initialPages?: StreamingPage[];
  title?: string;
  /** Fired once with the materialised book when streaming completes. */
  onBookComplete?: (book: Book) => void;
}

function NextPageSkeleton() {
  return (
    <div
      aria-hidden="true"
      className="kid-card"
      style={{ padding: "1.5rem", opacity: 0.5 }}
    >
      <div
        className="shimmer"
        style={{ height: "1.5rem", borderRadius: "0.75rem", marginBottom: "0.75rem" }}
      />
      <div
        className="shimmer"
        style={{ height: "1.5rem", width: "80%", borderRadius: "0.75rem" }}
      />
    </div>
  );
}

export function BookReader({ streamUrl, initialPages, title, onBookComplete }: BookReaderProps) {
  const stream = useBookStream(streamUrl);
  const [pageIndex, setPageIndex] = useState(0);

  // Surface the completed book (with its real public id) to the parent.
  useEffect(() => {
    if (stream.book) onBookComplete?.(stream.book);
  }, [stream.book, onBookComplete]);

  const pages = useMemo<StreamingPage[]>(
    () => (streamUrl ? stream.pages : (initialPages ?? [])),
    [streamUrl, stream.pages, initialPages],
  );

  const totalPages = streamUrl ? stream.totalPages : (initialPages?.length ?? null);
  const current = pages[pageIndex];
  const hasNext =
    pageIndex < pages.length - 1 ||
    (totalPages != null && pageIndex < totalPages - 1);
  const nextIsReady = Boolean(pages[pageIndex + 1]?.text);

  // Keep the index in range as pages stream in.
  useEffect(() => {
    if (pageIndex > pages.length - 1 && pages.length > 0) {
      setPageIndex(pages.length - 1);
    }
  }, [pages.length, pageIndex]);

  if (stream.status === "error" && pages.length === 0) {
    return (
      <div role="alert" className="kid-card" style={{ padding: "1.5rem" }}>
        <p style={{ fontSize: "1.25rem" }}>
          😴 The storyteller is napping right now. Please try again in a moment.
        </p>
      </div>
    );
  }

  if (!current) {
    return (
      <div className="kid-card" style={{ padding: "1.5rem" }} aria-busy="true">
        <p style={{ fontSize: "1.25rem" }}>✨ Starting your story…</p>
        <NextPageSkeleton />
      </div>
    );
  }

  const pageLabel =
    totalPages != null
      ? `Page ${current.pageNumber} of ${totalPages}`
      : `Page ${current.pageNumber}`;

  // A missing illustration is still "drawing" only while the stream is live and
  // this page has not reported a failure; otherwise it has settled as missing.
  const streamActive =
    Boolean(streamUrl) &&
    (stream.status === "connecting" || stream.status === "streaming");

  return (
    <article aria-label={title ?? "Your book"}>
      {title && (
        <h1 style={{ fontFamily: "var(--font-reader)", fontSize: "2rem" }}>
          {title}
        </h1>
      )}

      <div
        key={current.pageNumber}
        style={{ display: "grid", gap: "1.25rem" }}
      >
        <ImagePlaceholder
          aspectRatio={current.aspectRatio}
          imageUrl={current.image?.url ?? null}
          alt={`Illustration for page ${current.pageNumber}`}
          expectsImage={current.expectsImage}
          generating={streamActive && !current.imageFailed}
        />

        <p
          aria-live="polite"
          style={{
            fontFamily: "var(--font-reader)",
            fontSize: "1.5rem",
            lineHeight: 1.7,
            margin: 0,
          }}
        >
          {current.text || "…"}
        </p>

        <ReadAloud text={current.text} />
      </div>

      {/* Optimistic next-page skeleton while the next page is still streaming. */}
      {hasNext && !nextIsReady && stream.status === "streaming" && (
        <div style={{ marginTop: "1.25rem" }}>
          <NextPageSkeleton />
        </div>
      )}

      <nav
        aria-label="Page navigation"
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: "1rem",
          marginTop: "1.5rem",
        }}
      >
        <button
          type="button"
          className="kid-button kid-button--primary"
          onClick={() => setPageIndex((i) => Math.max(0, i - 1))}
          disabled={pageIndex === 0}
        >
          ◀ Back
        </button>
        <span aria-live="polite" style={{ alignSelf: "center", fontWeight: 700 }}>
          {pageLabel}
        </span>
        <button
          type="button"
          className="kid-button kid-button--primary"
          onClick={() => setPageIndex((i) => Math.min(pages.length - 1, i + 1))}
          disabled={pageIndex >= pages.length - 1}
        >
          Next ▶
        </button>
      </nav>
    </article>
  );
}
